import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import { alpha } from '@mui/material/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { Button } from '@/shared/components/ui';
import { userApi } from '@/features/users/api/user.api';
import { profileInitials } from '@/features/users/utils/profileNames';
import type { User } from '@/types';

interface TravelerAvatarUploadProps {
  user: User;
  onUpdated: (user: User) => void;
  size?: number;
}

export function TravelerAvatarUpload({ user, onUpdated, size = 84 }: TravelerAvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setUploading(true);

    try {
      const updatedUser = await userApi.uploadAvatar(file);
      onUpdated(updatedUser);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed. Please try again.';
      setError(message);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={user.avatar}
          sx={{
            width: size,
            height: size,
            bgcolor: 'primary.main',
            fontSize: size * 0.34,
            fontWeight: 700,
            border: '3px solid',
            borderColor: 'background.paper',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          {profileInitials(user)}
        </Avatar>

        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.5),
              borderRadius: '50%',
            }}
          >
            <CircularProgress size={28} sx={{ color: 'white' }} />
          </Box>
        )}

        <Box
          component="label"
          htmlFor="traveler-avatar-input"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 30,
            height: 30,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: uploading ? 'default' : 'pointer',
            boxShadow: 2,
            border: '2px solid',
            borderColor: 'background.paper',
            '&:hover': { bgcolor: uploading ? 'primary.main' : 'primary.dark' },
          }}
        >
          <CameraAltIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>

      <input
        id="traveler-avatar-input"
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={uploading}
      />

      <Button
        size="small"
        intent="secondary-outlined"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        sx={{ mt: 0.5 }}
      >
        {user.avatar ? 'Change photo' : 'Add photo'}
      </Button>

      {error && <FormHelperText error>{error}</FormHelperText>}

      <Typography variant="caption" color="text.secondary" textAlign="center">
        JPG, PNG or WEBP · max 10MB
      </Typography>
    </Box>
  );
}
